import React from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';

const renderYAxisTick = ({ x, y, payload }) => (
  <foreignObject x={x - 190} y={y - 10} width={180} height={20}>
    <div
      xmlns="http://www.w3.org/1999/xhtml"
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
        fontSize: '12px',
        textAlign: 'right',
        lineHeight: '20px',
      }}
      title={payload.value}
    >
      {payload.value}
    </div>
  </foreignObject>
);

const CustomTooltip = ({ active, payload, label, color, stacked, baseLabel, acceptedLabel, declinedLabel }) => {
  if (!active || !payload?.length) return null;

  const visiblePayload = payload.filter((item) => item && item.value !== undefined);
  const first = visiblePayload[0];
  const { value } = first || {};

  return (
    <Paper
      elevation={3}
      sx={{
        minWidth: 220,
        border: `2px solid ${color}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box p={1}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary', textAlign: 'left' }}>
          {label}
        </Typography>

        <Divider sx={{ m: 1 }} />

        {stacked ? (
          <>
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: color,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {baseLabel}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight="bold" color="text.primary">
                {payload.find((item) => item.dataKey === 'remainingReferrals')?.value ?? 0}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: '#2ecc71',
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {acceptedLabel}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight="bold" color="text.primary">
                {payload.find((item) => item.dataKey === 'acceptedReferrals')?.value ?? 0}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: '#e74c3c',
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {declinedLabel}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight="bold" color="text.primary">
                {payload.find((item) => item.dataKey === 'declinedReferrals')?.value ?? 0}
              </Typography>
            </Box>
          </>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: color,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Referrals
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="bold" color="text.primary">
              {value}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default function ReferralBarChart({
  referrals,
  color,
  totalLabel,
  show,
  noDataText = 'No data found',
  stacked = false,
  baseLabel = 'Referrals',
  declinedLabel = 'Declined',
  acceptedLabel = 'Accepted',
}) {
  if (!show) {
    return <Typography>{noDataText}</Typography>;
  }

  const renderValueLabel = (dataKey) => ({ x, y, width, height, index }) => {
    const row = referrals[index] || {};
    const segmentValue =
      dataKey === 'declinedReferrals'
        ? Number(row.declinedReferrals ?? row.declined_referrals ?? 0)
        : dataKey === 'acceptedReferrals'
        ? Number(row.acceptedReferrals ?? row.accepted_referrals ?? 0)
        : Number(row.remainingReferrals ?? row.referrals ?? 0);

    if (!segmentValue || segmentValue === 0) {
      return null;
    }

    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill="#fff"
        fontSize={12}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {segmentValue}
      </text>
    );
  };

  const stackedTotals = stacked
    ? referrals.reduce(
        (acc, row) => {
          const declined = Number(row.declinedReferrals ?? row.declined_referrals ?? 0);
          const accepted = Number(row.acceptedReferrals ?? row.accepted_referrals ?? 0);
          const remaining = Number(row.remainingReferrals ?? row.referrals ?? 0);

          acc.accepted += accepted;
          acc.declined += declined;
          acc.remaining += remaining;
          return acc;
        },
        { accepted: 0, declined: 0, remaining: 0 }
      )
    : null;

  return (
    <>
      {!stacked && totalLabel && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              backgroundColor: color,
            }}
          />
          <Typography variant="h6" color="text.primary" sx={{ fontSize: 20 }}>
            {totalLabel}
          </Typography>
        </Box>
      )}

      {stacked && stackedTotals && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          {totalLabel && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: color,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {totalLabel}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: color,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {baseLabel} ({stackedTotals.remaining})
            </Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: '#2ecc71',
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {acceptedLabel} ({stackedTotals.accepted})
            </Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: '#e74c3c',
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {declinedLabel} ({stackedTotals.declined})
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ height: Math.max(referrals.length * 40, 500), width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={referrals} layout="vertical">
            <XAxis type="number" domain={[0, 'dataMax']} allowDecimals={false} />
            <YAxis type="category" width={200} dataKey="name" tick={renderYAxisTick} />
            <Tooltip content={<CustomTooltip color={color} stacked={stacked} baseLabel={baseLabel} acceptedLabel={acceptedLabel} declinedLabel={declinedLabel} />} />
            {stacked ? (
              <>
                <Bar
                  dataKey="remainingReferrals"
                  fill={color}
                  stackId="a"
                  isAnimationActive={false}
                  activeBar={false}
                  onMouseOver={() => { }}
                  onMouseLeave={() => { }}
                >
                  <LabelList content={renderValueLabel('remainingReferrals')} />
                </Bar>
                <Bar
                  dataKey="acceptedReferrals"
                  fill="#2ecc71"
                  stackId="a"
                  isAnimationActive={false}
                  activeBar={false}
                  onMouseOver={() => { }}
                  onMouseLeave={() => { }}
                >
                  <LabelList content={renderValueLabel('acceptedReferrals')} />
                </Bar>
                <Bar
                  dataKey="declinedReferrals"
                  fill="#e74c3c"
                  stackId="a"
                  isAnimationActive={false}
                  activeBar={false}
                  onMouseOver={() => { }}
                  onMouseLeave={() => { }}
                >
                  <LabelList content={renderValueLabel('declinedReferrals')} />
                </Bar>
              </>
            ) : (
              <Bar
                dataKey="referrals"
                fill={color}
                isAnimationActive={false}
                activeBar={false}
                onMouseOver={() => { }}
                onMouseLeave={() => { }}
              >
                <LabelList dataKey="referrals" position="insideRight" fill="#fff" />
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
}
